import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Route, Routes, useNavigate, useLocation, useParams, Navigate } from "react-router-dom";
import { Alert } from "@ann/web-components";
import { KnowledgeHubContainer, RoutesContainer, RoutesOutlet } from "./KnowledgeHub.styled";
import { Overview, Facts, FactDetail, TrainProgress, Users, Suggestions, ActivityLog } from "./pages";
import { Header, Tabs, FactsTrainingModal,  CreateFactModal } from "./components";
import { CreateSuggestionModal } from "./pages/Suggestions/components";
import TrainModal from "../../components/TrainModal";
import UploadFactsModal from "../../components/UploadFactsModal";
import AIMSDriveModal from "../../components/AIMSDriveModal";
import ShareKnowledgeModal from "../../components/ShareKnowledgeModal";
import { KnowledgeHubTabsEnum } from "./enums/knowledgeHubTabs";
import { FactsProvider, SuggestionsProvider, useSuggestionsContext, MyMemberProvider, useMyMemberContext, MembersProvider, useFactsContext } from "./context";
import { getKnowledgeByUuid, listKnowledge } from "../../services/knowledge";

const KnowledgeHub = ({ breadcrumbHelpers }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { knowledgeId } = useParams();
  const [selectedTab, setSelectedTab] = useState(KnowledgeHubTabsEnum.OVERVIEW);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [knowledgeData, setKnowledgeData] = useState(null);
  const [knowledgeBasesList, setKnowledgeBasesList] = useState([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isTrainModalOpen, setIsTrainModalOpen] = useState(false);
  const [isUploadFactsModalOpen, setIsUploadFactsModalOpen] = useState(false);
  const [isAIMSDriveModalOpen, setIsAIMSDriveModalOpen] = useState(false);
  const [isFactsTrainingModalOpen, setIsFactsTrainingModalOpen] = useState(false);
  const [isCreateFactModalOpen, setIsCreateFactModalOpen] = useState(false);
  const [isCreateSuggestionModalOpen, setIsCreateSuggestionModalOpen] = useState(false);
  const [selectedFactForSuggestion, setSelectedFactForSuggestion] = useState(null);
  const [lockFactForSuggestion, setLockFactForSuggestion] = useState(false);
  
  const knowledgeName = knowledgeData?.name || location.state?.knowledgeName || "Sales Config";

  // Fetch knowledge bases list for CreateFactModal
  const fetchKnowledgeBasesList = useCallback(async () => {
    try {
      const response = await listKnowledge({
        page: 1,
        page_size: 100, // Get a reasonable number for the dropdown
        order_by: "name",
        order_direction: "ASC",
      });

      if (response.body && Array.isArray(response.body)) {
        // Pass API response directly - modal handles the format
        setKnowledgeBasesList(response.body);
      }
    } catch (err) {
      console.error("Error fetching knowledge bases list:", err);
      // On error, set empty array so modal can still use current knowledgeBase prop
      setKnowledgeBasesList([]);
    }
  }, []);

  useEffect(() => {
    fetchKnowledgeBasesList();
  }, [fetchKnowledgeBasesList]);

  // Fetch knowledge data when knowledgeId changes
  const fetchKnowledgeData = useCallback(async () => {
    if (!knowledgeId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getKnowledgeByUuid({ knowledgebase_uuid: knowledgeId });

      if (response.body) {
        const mappedData = {
          name: response.body.name ,
          description: response.body.description,
          status: response.body.status,
          createdBy: response.body.createdBy || response.body.created_by,
          createdAt: response.body.createdAt || response.body.created_at,
          updated_at: response.body.lastUpdated || response.body.last_updated || response.body.updatedAt || response.body.updated_at,
          created_at: response.body.createdAt || response.body.created_at,
          department_uuid: response.body.department_uuid || response.body.departmentUuid,
          owner_uuid: response.body.owner_uuid || response.body.ownerUuid,
        };

        setKnowledgeData(mappedData);
      }
    } catch (err) {
      console.error("Error fetching knowledge data:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [knowledgeId]);

  useEffect(() => {
    fetchKnowledgeData();
  }, [fetchKnowledgeData]);

  const isFactDetailRoute = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const factsIndex = pathParts.indexOf('facts');
    
    
    if (factsIndex !== -1 && pathParts.length > factsIndex + 2) {
      
      const segmentAfterFacts = pathParts[factsIndex + 1];
      const segmentAfterThat = pathParts[factsIndex + 2];
      
      
      if (segmentAfterFacts && segmentAfterThat) {
        return true;
      }
    }
    
    return false;
  }, [location.pathname]);

  // Mapeo de tabs a rutas URL
  const getTabRoute = (tabIndex) => {
    switch (tabIndex) {
      case KnowledgeHubTabsEnum.OVERVIEW:
        return "";
      case KnowledgeHubTabsEnum.FACTS:
        return "facts";
      case KnowledgeHubTabsEnum.TRAIN_PROGRESS:
        return "train-progress";
      case KnowledgeHubTabsEnum.USERS:
        return "users";
      case KnowledgeHubTabsEnum.SUGGESTIONS:
        return "suggestions";
      case KnowledgeHubTabsEnum.ACTIVITY_LOG:
        return "activity-log";
      default:
        return "";
    }
  };

  const getTabFromRoute = (pathname) => {
    if (pathname.includes("/facts")) {
      return KnowledgeHubTabsEnum.FACTS;
    } else if (pathname.includes("/train-progress")) {
      return KnowledgeHubTabsEnum.TRAIN_PROGRESS;
    } else if (pathname.includes("/users")) {
      return KnowledgeHubTabsEnum.USERS;
    } else if (pathname.includes("/suggestions")) {
      return KnowledgeHubTabsEnum.SUGGESTIONS;
    } else if (pathname.includes("/activity-log")) {
      return KnowledgeHubTabsEnum.ACTIVITY_LOG;
    } else {
      return KnowledgeHubTabsEnum.OVERVIEW;
    }
  };

  useEffect(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    setSelectedTab(tabFromUrl);
  }, [location.pathname]);

  useEffect(() => {
    breadcrumbHelpers?.setPageBreadcrumb?.(null, knowledgeName, {
      pageHref: "/v2/knowledge",
      subHref: location.pathname,
    });
  }, [breadcrumbHelpers, location.pathname, knowledgeName]);

  const handleTabChange = (index) => {
    setSelectedTab(index);
    const newRoute = getTabRoute(index);
    
    // Extraer el base path hasta el knowledgeId
    const pathParts = location.pathname.split('/').filter(Boolean);
    const knowledgeHubIndex = pathParts.indexOf('knowledge-hub');
    const basePath = '/' + pathParts.slice(0, knowledgeHubIndex + 2).join('/');
    
    if (newRoute) {
      navigate(`${basePath}/${newRoute}`, { replace: true });
    } else {
      navigate(basePath, { replace: true });
    }
  };

  const handleMenuChange = (value) => {
    if (value === 'add-facts') {
      setIsCreateFactModalOpen(true);
    } else if (value === 'add-users') {
      setIsShareModalOpen(true);
    } else if (value === 'train') {
      setIsTrainModalOpen(true);
    }
  };

  const handleShareModalClose = () => {
    setIsShareModalOpen(false);
  };

  const handleShare = (users) => {
    console.log('Shared with users:', users);
    // TODO: Implement share functionality
  };

  const handleTrainModalClose = () => {
    setIsTrainModalOpen(false);
  };

  const handleUploadDocuments = () => {
    setIsTrainModalOpen(false);
    setIsUploadFactsModalOpen(true);
  };

  const handleUploadFactsModalClose = () => {
    setIsUploadFactsModalOpen(false);
    setIsTrainModalOpen(true);
  };

  const handleUploadStart = () => {
    // Close both modals when upload starts
    setIsUploadFactsModalOpen(false);
    setIsTrainModalOpen(false);
  };

  const handleAIMSDrive = () => {
    setIsTrainModalOpen(false);
    setIsAIMSDriveModalOpen(true);
  };

  const handleAIMSDriveModalClose = () => {
    setIsAIMSDriveModalOpen(false);
    setIsTrainModalOpen(true);
  };

  const handleContinueInterview = () => {
    setIsTrainModalOpen(false);
    setIsFactsTrainingModalOpen(true);
  };

  const handleFactsTrainingModalClose = () => {
    setIsFactsTrainingModalOpen(false);
    setIsTrainModalOpen(true);
  };
  const handleCreateFactModalClose = () => {
    setIsCreateFactModalOpen(false);
  };

  const handleCreateSuggestionModalClose = useCallback(() => {
    setIsCreateSuggestionModalOpen(false);
    setSelectedFactForSuggestion(null);
    setLockFactForSuggestion(false);
  }, []);

  const handleAddSuggestion = useCallback((fact = null, lockFact = false) => {
    setSelectedFactForSuggestion(fact);
    setLockFactForSuggestion(lockFact);
    setIsCreateSuggestionModalOpen(true);
  }, []);

  return (
    <KnowledgeHubContainer>
      {!isFactDetailRoute && (
        <>
          <Header title={knowledgeName} onMenuChange={handleMenuChange} />
          <Tabs key={selectedTab} onTabChange={handleTabChange} defaultIndex={selectedTab} />
        </>
      )}
      <MyMemberProvider>
        <MembersProvider>
          <FactsProvider>
            <SuggestionsProvider>
              <KnowledgeHubContent
                breadcrumbHelpers={breadcrumbHelpers}
                knowledgeData={knowledgeData}
                loading={loading}
                error={error}
                handleAddSuggestion={handleAddSuggestion}
                handleCreateSuggestionModalClose={handleCreateSuggestionModalClose}
                isCreateSuggestionModalOpen={isCreateSuggestionModalOpen}
                selectedFactForSuggestion={selectedFactForSuggestion}
                lockFactForSuggestion={lockFactForSuggestion}
              />
              <CreateFactModalWrapper
                knowledgeBaseUuid={knowledgeId}
                isOpen={isCreateFactModalOpen}
                onClose={handleCreateFactModalClose}
                knowledgeBase={{ 
                  name: knowledgeName,
                  description: knowledgeData?.description || ''
                }}
                knowledgeBases={knowledgeBasesList}
              />
            </SuggestionsProvider>
          </FactsProvider>
          <ShareKnowledgeModal
            isOpen={isShareModalOpen}
            onClose={handleShareModalClose}
            knowledgeBase={{ name: knowledgeName }}
            onShare={handleShare}
          />
        </MembersProvider>
      </MyMemberProvider>
      <TrainModal
        isOpen={isTrainModalOpen}
        onClose={handleTrainModalClose}
        knowledgeBase={{ name: knowledgeName }}
        onUploadDocuments={handleUploadDocuments}
        onAIMSDrive={handleAIMSDrive}
        onContinueInterview={handleContinueInterview}
      />
      <FactsTrainingModal
        isOpen={isFactsTrainingModalOpen}
        onClose={handleFactsTrainingModalClose}
      />
      <UploadFactsModal
        isOpen={isUploadFactsModalOpen}
        onClose={handleUploadFactsModalClose}
        knowledgeBase={{ name: knowledgeName }}
        onUploadStart={handleUploadStart}
      />
      <AIMSDriveModal
        isOpen={isAIMSDriveModalOpen}
        onClose={handleAIMSDriveModalClose}
        knowledgeBase={{ name: knowledgeName }}
      />
    </KnowledgeHubContainer>
  );
};

// Componente wrapper para CreateFactModal que tiene acceso al FactsContext
const CreateFactModalWrapper = ({
  isOpen,
  onClose,
  knowledgeBase,
  knowledgeBaseUuid,
  knowledgeBases,
  
}) => {
  const { knowledgeId } = useParams();
  const { fetchFacts } = useFactsContext();

  const handleCreate = useCallback((newFact) => {
    // Refrescar la lista de facts con parámetros por defecto
    if (knowledgeId) {
      fetchFacts(
        {
          knowledgebase_uuid: knowledgeId,
          page: 1,
          page_size: 20,
          order_by: 'updated_at',
          order_direction: 'DESC',
        },
        { persistAsBase: true }
      );
    }
  }, [knowledgeId, fetchFacts]);

  return (
    <CreateFactModal
      knowledgeBaseUuid={knowledgeBaseUuid}
      isOpen={isOpen}
      onClose={onClose}
      knowledgeBase={knowledgeBase}
      knowledgeBases={knowledgeBases}
      onCreate={handleCreate}
    />
  );
};

// Componente interno que tiene acceso al contexto de Suggestions
const KnowledgeHubContent = ({
  breadcrumbHelpers,
  knowledgeData,
  loading,
  error,
  handleAddSuggestion,
  handleCreateSuggestionModalClose,
  isCreateSuggestionModalOpen,
  selectedFactForSuggestion,
  lockFactForSuggestion,
}) => {
  const { knowledgeId } = useParams();
  const { fetchMyMember } = useMyMemberContext();
  const { alert, clearAlert } = useSuggestionsContext();

  // Cargar miembro automáticamente al montar el componente
  useEffect(() => {
    if (knowledgeId) {
      fetchMyMember(knowledgeId);
    }
  }, [knowledgeId, fetchMyMember]);

  return (
    <>
      {alert && (
        <Alert
          title={alert.title}
          description={alert.description}
          severity={alert.severity}
          maxWidth="500px"
          closable={true}
          autoClose={true}
          autoCloseDelay={5000}
          onClose={clearAlert}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
          }}
        />
      )}
      <RoutesContainer>
        <RoutesOutlet>
          <Routes>
            <Route index element={<Overview knowledgeData={knowledgeData} loading={loading} error={error} />} />
            <Route path="facts/:factId/*" element={<FactDetail breadcrumbHelpers={breadcrumbHelpers} onAddSuggestion={handleAddSuggestion} />} />
            <Route path="facts" element={<Facts onSuggestUpdate={handleAddSuggestion} />} />
            <Route path="train-progress" element={<TrainProgress />} />
            <Route path="users" element={<Users />} />
            <Route path="suggestions" element={<Suggestions onAddSuggestion={handleAddSuggestion} />} />
            <Route path="activity-log" element={<ActivityLog />} />
            {/* <Route path="*" element={<Navigate to="" replace />} /> */}
          </Routes>
        </RoutesOutlet>
      </RoutesContainer>
      <CreateSuggestionModal
        isOpen={isCreateSuggestionModalOpen}
        onClose={handleCreateSuggestionModalClose}
        initialFact={selectedFactForSuggestion}
        lockFact={lockFactForSuggestion}
      />
    </>
  );
};

export default KnowledgeHub;

